import { coverSourceRect, type FocalPoint, type SourceRect } from '@/shared/image/cropGeometry'
import type { ImageMeta } from '@/shared/image/filePreflight'
import {
  SOCIAL_RESIZE_PRESETS,
  socialResizePreset,
  type SocialResizePresetId,
} from './presets'

export { coverSourceRect }
export type { FocalPoint, SourceRect }

export const SOCIAL_RESIZE_LIMITS = {
  maxSources: 4,
  maxPresets: 6,
  maxOutputs: 24,
  maxFileBytes: 20 * 1024 * 1024,
  maxSourcePixels: 24_000_000,
  maxAggregatePixels: 48_000_000,
  maxOutputPixels: 12_000_000,
  maxLongestSide: 8192,
  maxZipBytes: 64 * 1024 * 1024,
  concurrency: 1,
} as const

export type SocialResizeLimitCode =
  | 'source_count'
  | 'preset_count'
  | 'output_count'
  | 'file_size'
  | 'source_pixels'
  | 'aggregate_pixels'
  | 'output_pixels'
  | 'longest_side'

export class SocialResizeLimitError extends Error {
  constructor(readonly code: SocialResizeLimitCode) {
    super(code)
    this.name = 'SocialResizeLimitError'
  }
}

export interface SocialResizeSourceMeta extends ImageMeta {
  id: string
}

export function validateSocialResizeSelection(
  sources: readonly SocialResizeSourceMeta[],
  presetIds: readonly SocialResizePresetId[],
): void {
  if (sources.length < 1 || sources.length > SOCIAL_RESIZE_LIMITS.maxSources) {
    throw new SocialResizeLimitError('source_count')
  }
  if (presetIds.length < 1 || presetIds.length > SOCIAL_RESIZE_LIMITS.maxPresets) {
    throw new SocialResizeLimitError('preset_count')
  }
  if (sources.length * presetIds.length > SOCIAL_RESIZE_LIMITS.maxOutputs) {
    throw new SocialResizeLimitError('output_count')
  }

  let aggregatePixels = 0
  for (const source of sources) {
    if (source.bytes > SOCIAL_RESIZE_LIMITS.maxFileBytes) {
      throw new SocialResizeLimitError('file_size')
    }
    const pixels = source.width * source.height
    if (!Number.isSafeInteger(pixels) || pixels > SOCIAL_RESIZE_LIMITS.maxSourcePixels) {
      throw new SocialResizeLimitError('source_pixels')
    }
    if (Math.max(source.width, source.height) > SOCIAL_RESIZE_LIMITS.maxLongestSide) {
      throw new SocialResizeLimitError('longest_side')
    }
    aggregatePixels += pixels
  }
  if (!Number.isSafeInteger(aggregatePixels) || aggregatePixels > SOCIAL_RESIZE_LIMITS.maxAggregatePixels) {
    throw new SocialResizeLimitError('aggregate_pixels')
  }

  for (const presetId of presetIds) {
    const preset = socialResizePreset(presetId)
    const pixels = preset.width * preset.height
    if (
      pixels > SOCIAL_RESIZE_LIMITS.maxOutputPixels ||
      Math.max(preset.width, preset.height) > SOCIAL_RESIZE_LIMITS.maxLongestSide
    ) {
      throw new SocialResizeLimitError(
        pixels > SOCIAL_RESIZE_LIMITS.maxOutputPixels ? 'output_pixels' : 'longest_side',
      )
    }
  }
}

export function clampOutputPan(focal: FocalPoint): FocalPoint {
  return {
    x: Math.min(1, Math.max(0, focal.x)),
    y: Math.min(1, Math.max(0, focal.y)),
  }
}

export function safeFilenamePart(value: string, fallback = 'image'): string {
  const normalized = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\.[^.]+$/, '')
    .replace(/[^a-zA-Z0-9\u0400-\u04ff_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
  return normalized || fallback
}

export function outputFilename(
  sourceName: string,
  presetId: SocialResizePresetId,
  extension: 'jpg' | 'png' = 'jpg',
  sourceSuffix?: string,
): string {
  const suffix = sourceSuffix ? `-${safeFilenamePart(sourceSuffix, 'source')}` : ''
  return `${safeFilenamePart(sourceName)}${suffix}-${presetId}.${extension}`
}

export function buildOutputJobs(
  sources: readonly { id: string; name: string; focal: FocalPoint }[],
  presetIds: readonly SocialResizePresetId[],
): Array<{
  id: string
  sourceId: string
  presetId: SocialResizePresetId
  focal: FocalPoint
  filename: string
}> {
  return sources.flatMap((source) => presetIds.map((presetId) => ({
    id: `${source.id}:${presetId}`,
    sourceId: source.id,
    presetId,
    focal: clampOutputPan(source.focal),
    filename: outputFilename(source.name, presetId, 'jpg', source.id),
  })))
}

export function presetRegistryIsBounded(): boolean {
  return SOCIAL_RESIZE_PRESETS.length === 6
    && SOCIAL_RESIZE_PRESETS.every((preset) =>
      preset.width * preset.height <= SOCIAL_RESIZE_LIMITS.maxOutputPixels,
    )
}
